import { getLink } from "@utils/linkUtils";
import { motion } from "framer-motion";
import { useLocale, useTranslations } from "next-intl";
import Link from "next/link";
import React, { FC, Fragment, useEffect, useRef, useState } from "react";
import ReactFocusLock from "react-focus-lock";

import useBreakPoint from "@/hooks/useBreakpoint";
import { NavigationItem, NavigationStructure } from "@/types";

import Button, { ButtonSize, ButtonTheme, ButtonVariant } from "../ui/Button/Button";
import Icon from "../ui/Icon/Icon";

interface Props {
  menu: NavigationStructure;
  desktopPanelID: string;
  panelVariation: any;
  transition: any;
  setDesktopPanelID: Function;
  trigger: any;
  setTrigger: Function;
  firstLayerRef: React.RefObject<HTMLDivElement>;
}
interface PanelProps {
  item: NavigationItem;
  setDesktopPanelID: Function;
  left?: number;
}
const NavigationCorp: FC<Props> = ({ menu, desktopPanelID, panelVariation, transition, setDesktopPanelID, trigger, setTrigger, firstLayerRef }) => {
  const { isMd } = useBreakPoint("md");
  const [buttonsPosition, setButtonsPosition] = useState<number[]>();

  useEffect(() => {
    if (firstLayerRef.current && isMd) {
      const buttons = Array.from(firstLayerRef.current.querySelectorAll("button"));
      setButtonsPosition(
        buttons.map((item) => {
          return item.getBoundingClientRect().left;
        }),
      );
    }
  }, [isMd]);

  return (
    <>
      {desktopPanelID && (
        <div
          onClick={() => {
            setDesktopPanelID("");
          }}
          className="absolute top-[100%] w-full h-[calc(100dvh-96px)]"
        ></div>
      )}
      {menu.items.map((item, index) => {
        const active = desktopPanelID === item.id;
        return (
          <motion.div
            id={`modal-panel-desktop-` + item.id + "-" + index}
            initial="close"
            variants={{
              open: {
                opacity: 1,
                transform: "translateY(0px)",
                display: "block",
              },
              close: {
                opacity: 0,
                transform: "translateY(-50px)",
                transitionEnd: {
                  display: "none",
                },
              },
            }}
            key={"wrapper-" + item.id}
            animate={active ? "open" : "close"}
            transition={transition}
            className="pointer-events-auto"
          >
            <ReactFocusLock
              returnFocus={(el) => {
                if (!trigger) {
                  setTrigger(el as HTMLElement);
                } else {
                  trigger.focus();
                  setTrigger(undefined);
                }
                return true;
              }}
              disabled={!active}
            >
              <PanelCorp setDesktopPanelID={setDesktopPanelID} item={item} left={buttonsPosition && buttonsPosition[index]} />
            </ReactFocusLock>
          </motion.div>
        );
      })}
    </>
  );
};

const PanelCorp: FC<PanelProps> = ({ item, setDesktopPanelID, left }) => {
  const locale = useLocale();
  const ref = useRef<HTMLDivElement>(null);

  const PADDING = 24;
  useEffect(() => {
    if (ref.current && left) {
      ref.current.style.left = left - PADDING + "px";
    }
  }, [left, ref]);

  return (
    <div className="bg-white p-l pt-xs rounded-2xl absolute top-m shadow-[0_2px_24px_0_rgba(0,0,0,0.1)]" ref={ref}>
      <div className="w-[250px] max-h-[calc(100dvh-var(--header-height)-53px)] relative overflow-y-auto">
        {item && item.columns && item.columns.length > 0 && (
          <>
            <div className="sticky top-0 bg-white pt-l">
              {!item.menuItemCta && <p className="mb-m font-secondary text-s font-medium">{item.columns[0].title}</p>}
              {item.menuItemCta && (
                <div className="mb-m">
                  <Link href={getLink(item.menuItemCta, locale)} className="font-secondary text-s font-medium">
                    {item.menuItemCta.label}
                  </Link>
                </div>
              )}

              <hr className="border-t-gray-10" />
            </div>
            <div className="flex flex-col gap-0 justify-between mt-s">
              {item.columns.map((column) => {
                return (
                  <ul key={"column-" + column.id}>
                    {column.links?.map((link, index) => {
                      return (
                        <li key={`desktop-menu-link-${link.id}-${index}`}>
                          <Button
                            className={`text-s py-s w-full no-underline flex justify-between items-center font-secondary font-medium hover:underline`}
                            variant={ButtonVariant.TERTIARY}
                            size={ButtonSize.SMALL}
                            theme={ButtonTheme.BLACK}
                            href={getLink(link, locale)}
                          >
                            <>{link.label}</>
                          </Button>
                        </li>
                      );
                    })}
                  </ul>
                );
              })}
            </div>
          </>
        )}

        <button
          className="absolute right-m top-m sr-only"
          onClick={() => {
            setDesktopPanelID("");
          }}
        >
          <Icon name="close" className="stroke-black" size="24" title="Close" />
        </button>
      </div>
    </div>
  );
};
export default NavigationCorp;
